import React, { useState } from "react";
import { Plus, Trash2, Eye, Check, AlertCircle, Pencil } from "lucide-react";
import { useProject } from "../../state/ProjectContext";
import { Pill, Modal, Field } from "../ui-prim";
import { AIPanel } from "../AIPanel";
import { prompts } from "../../lib/ai";
import { toast } from "../../lib/utils";

const FIT = ["No Fit", "Partial Fit", "Full Fit"];
const fitTone = (f) => f === "Full Fit" ? "green" : f === "Partial Fit" ? "purple" : "red";
const sevTone = (x) => x === "Critical" ? "red" : x === "High" ? "amber" : x === "Medium" ? "blue" : "neutral";
const statusTone = (s) => s === "Closed" ? "green" : s === "In Progress" ? "blue" : "amber";

export default function GapFit() {
  const { doc, mutate, readOnly } = useProject();
  const [filter, setFilter] = useState("All");
  const [showAdd, setShowAdd] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);

  const list = filter === "All" ? doc.gaps : doc.gaps.filter(g => g.fitStatus === filter);
  const noFit = doc.gaps.filter(g => g.fitStatus === "No Fit").length;
  const part  = doc.gaps.filter(g => g.fitStatus === "Partial Fit").length;
  const full  = doc.gaps.filter(g => g.fitStatus === "Full Fit").length;
  const closed = doc.gaps.filter(g => g.status === "Closed").length;

  const addGap = () => {
    const g = (k) => document.getElementById("ng-" + k)?.value;
    const area = g("area");
    if (!area) return toast("Area required");
    mutate(d => ({
      ...d, gaps: [...d.gaps, {
        id: "G" + Date.now(), area,
        requirement: g("req") || "—",
        capability: g("cap") || "—",
        gap: g("gap") || "",
        action: g("action") || "",
        fitStatus: g("fit"),
        impact: g("impact"),
        priority: g("prio"),
        owner: g("own") || "TBD",
        status: "Open",
      }],
    }));
    setShowAdd(false);
  };

  const removeGap = (id) => {
    if (!window.confirm("Delete this gap?")) return;
    mutate(d => ({ ...d, gaps: d.gaps.filter(g => g.id !== id) }));
    setViewing(null);
  };

  const updateStatus = (id, status) => {
    mutate(d => ({ ...d, gaps: d.gaps.map(g => g.id === id ? { ...g, status } : g) }));
  };

  const saveEdit = () => {
    if (!editing) return;
    const g = (k) => document.getElementById("eg-" + k)?.value;
    mutate(d => ({
      ...d,
      gaps: d.gaps.map(x => x.id === editing.id ? {
        ...x,
        area:        g("area")   ?? x.area,
        requirement: g("req")    ?? x.requirement,
        capability:  g("cap")    ?? x.capability,
        gap:         g("gap")    ?? x.gap,
        action:      g("action") ?? x.action,
        fitStatus:   g("fit")    ?? x.fitStatus,
        impact:      g("impact") ?? x.impact,
        priority:    g("prio")   ?? x.priority,
        owner:       g("own")    ?? x.owner,
        status:      g("stat")   ?? x.status,
      } : x),
    }));
    setEditing(null);
    toast("Saved");
  };

  return (
    <>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Capability Assessment</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Gap / Fit Analysis</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {["All", ...FIT].map(f => (
            <button key={f} className={`btn ${filter === f ? "btn-primary" : "btn-secondary"} btn-sm`} onClick={() => setFilter(f)} data-testid={`filter-gap-${f.replace(/ /g, '')}`}>{f}</button>
          ))}
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)} data-testid="btn-add-gap" disabled={readOnly} style={readOnly ? { display: "none" } : {}}><Plus size={12}/> Add Gap</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Sum label="No Fit" value={noFit} color="#EF4444" sub="Full capability gap"/>
        <Sum label="Partial Fit" value={part} color="#A855F7" sub="Partial coverage"/>
        <Sum label="Full Fit" value={full} color="#22C55E" sub="No gap"/>
        <Sum label="Gaps Closed" value={closed} color="#3B82F6" sub={`of ${doc.gaps.length} total`}/>
      </div>

      <div className="card p-5 mb-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-3">Fit Coverage Overview</div>
        <div className="h-6 rounded-sm overflow-hidden flex bg-zinc-100 border border-[var(--border)]">
          {full > 0 && <div style={{ flex: full, background: "rgba(34,197,94,.55)" }}  className="grid place-items-center font-mono text-[10px] text-green-700">{full} Full</div>}
          {part > 0 && <div style={{ flex: part, background: "rgba(168,85,247,.45)" }} className="grid place-items-center font-mono text-[10px] text-purple-800">{part} Partial</div>}
          {noFit > 0 && <div style={{ flex: noFit, background: "rgba(239,68,68,.5)" }}  className="grid place-items-center font-mono text-[10px] text-red-200">{noFit} No Fit</div>}
        </div>
      </div>

      <AIPanel title="Gap Analysis" section="gap" buildPrompt={() => prompts.gapAnalysis(doc)} buttonLabel="Analyze Gaps"/>

      <div className="card overflow-x-auto mb-5">
        <table className="tbl">
          <thead><tr>
            <th>Area</th><th>Req</th><th>Capability</th><th>Fit</th><th>Gap</th><th>Impact</th><th>Priority</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {list.map(g => (
              <tr key={g.id}>
                <td className="text-sm font-medium text-zinc-900">{g.area}</td>
                <td className="font-mono text-xs text-zinc-500">{g.requirement}</td>
                <td className="text-xs text-zinc-500 max-w-[160px]">{g.capability}</td>
                <td><Pill tone={fitTone(g.fitStatus)}>{g.fitStatus}</Pill></td>
                <td className="text-xs text-zinc-500 max-w-[260px]">{g.gap.slice(0, 80)}{g.gap.length > 80 ? "…" : ""}</td>
                <td><Pill tone={sevTone(g.impact)}>{g.impact}</Pill></td>
                <td><Pill tone={sevTone(g.priority)}>{g.priority}</Pill></td>
                <td><Pill tone={statusTone(g.status)}>{g.status}</Pill></td>
                <td>
                  <div className="flex gap-1">
                    <button className="btn btn-ghost btn-sm" onClick={() => setViewing(g)} data-testid={`btn-view-gap-${g.id}`} title="View"><Eye size={11}/></button>
                    {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => setEditing(g)} data-testid={`btn-edit-gap-${g.id}`} title="Edit"><Pencil size={11}/></button>}
                    {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => removeGap(g.id)} title="Delete"><Trash2 size={11}/></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-3">Requirement — Capability Fit Matrix</div>
        <div className="grid grid-cols-1 gap-2">
          {doc.gaps.map(g => {
            const color = g.fitStatus === "Full Fit" ? "#22C55E" : g.fitStatus === "Partial Fit" ? "#A855F7" : "#EF4444";
            const Icon = g.fitStatus === "Full Fit" ? Check : g.fitStatus === "Partial Fit" ? AlertCircle : AlertCircle;
            return (
              <div key={g.id} className="flex items-start gap-3 p-3 border border-[var(--border)] rounded-sm bg-zinc-50">
                <div className="w-9 h-9 rounded-sm grid place-items-center"
                     style={{ background: color + "22", color, border: `1px solid ${color}66` }}>
                  <Icon size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-semibold text-zinc-900">{g.area}</span>
                    <Pill tone={fitTone(g.fitStatus)}>{g.fitStatus}</Pill>
                    <Pill tone="neutral">{g.requirement}</Pill>
                  </div>
                  <div className="text-xs text-zinc-500">{g.capability}</div>
                  {g.fitStatus !== "Full Fit" && (
                    <div className="text-[11px] text-zinc-500 mt-1 pt-1 border-t border-[var(--border)]">
                      <span style={{ color }} className="font-semibold">Gap:</span> {g.gap}
                    </div>
                  )}
                </div>
                <Pill tone={statusTone(g.status)}>{g.status}</Pill>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Gap Entry"
             footer={<><button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
                       <button className="btn btn-primary" onClick={addGap} data-testid="btn-save-gap">Add Gap</button></>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Area"><input id="ng-area" className="fld" placeholder="e.g. Authentication" data-testid="input-gap-area"/></Field>
          <Field label="Requirement ID"><input id="ng-req" className="fld" placeholder="BR-001"/></Field>
          <Field label="Current Capability" span={2}><input id="ng-cap" className="fld" placeholder="Existing system or tool"/></Field>
          <Field label="Gap Description" span={2}><textarea id="ng-gap" className="fld" rows={3}/></Field>
          <Field label="Recommended Action" span={2}><textarea id="ng-action" className="fld" rows={2}/></Field>
          <Field label="Fit Status">
            <select id="ng-fit" className="fld" defaultValue="No Fit">{FIT.map(o => <option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Impact">
            <select id="ng-impact" className="fld" defaultValue="High">{["Critical","High","Medium","Low"].map(o => <option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Priority">
            <select id="ng-prio" className="fld" defaultValue="High">{["Critical","High","Medium","Low"].map(o => <option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Owner"><input id="ng-own" className="fld"/></Field>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing ? `${viewing.area} — Gap Detail` : ""}>
        {viewing && (
          <>
            <div className="flex gap-2 mb-3 flex-wrap">
              <Pill tone={fitTone(viewing.fitStatus)}>{viewing.fitStatus}</Pill>
              <Pill tone="neutral">{viewing.requirement}</Pill>
              <Pill tone={statusTone(viewing.status)}>{viewing.status}</Pill>
            </div>
            <Field label="Current Capability"><div className="text-sm text-zinc-700">{viewing.capability}</div></Field>
            <Field label="Gap Description"><div className="text-sm text-zinc-700 bg-zinc-50 p-3 border border-[var(--border)] rounded-sm">{viewing.gap}</div></Field>
            <Field label="Recommended Action"><div className="text-sm text-zinc-700 bg-zinc-50 p-3 border border-[var(--border)] rounded-sm">{viewing.action}</div></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Owner"><div className="text-sm text-zinc-700">{viewing.owner}</div></Field>
              <Field label="Priority"><Pill tone={sevTone(viewing.priority)}>{viewing.priority}</Pill></Field>
            </div>
            <div className="flex items-center justify-between pt-2">
              <select className="fld max-w-[180px]" value={viewing.status} disabled={readOnly}
                      onChange={e => { updateStatus(viewing.id, e.target.value); setViewing({ ...viewing, status: e.target.value }); }}>
                {["Open", "In Progress", "Closed"].map(o => <option key={o}>{o}</option>)}
              </select>
              {!readOnly && <button className="btn btn-danger" onClick={() => removeGap(viewing.id)}><Trash2 size={11}/> Delete</button>}
            </div>
          </>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing ? `Edit Gap · ${editing.area}` : ""}
             footer={<><button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                       <button className="btn btn-primary" onClick={saveEdit} data-testid="btn-save-edit-gap">Save</button></>}>
        {editing && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Area"><input id="eg-area" className="fld" defaultValue={editing.area}/></Field>
            <Field label="Requirement ID"><input id="eg-req" className="fld" defaultValue={editing.requirement}/></Field>
            <Field label="Current Capability" span={2}><input id="eg-cap" className="fld" defaultValue={editing.capability}/></Field>
            <Field label="Gap Description" span={2}><textarea id="eg-gap" className="fld" rows={3} defaultValue={editing.gap}/></Field>
            <Field label="Recommended Action" span={2}><textarea id="eg-action" className="fld" rows={2} defaultValue={editing.action}/></Field>
            <Field label="Fit Status">
              <select id="eg-fit" className="fld" defaultValue={editing.fitStatus}>{FIT.map(o => <option key={o}>{o}</option>)}</select>
            </Field>
            <Field label="Impact">
              <select id="eg-impact" className="fld" defaultValue={editing.impact}>{["Critical","High","Medium","Low"].map(o => <option key={o}>{o}</option>)}</select>
            </Field>
            <Field label="Priority">
              <select id="eg-prio" className="fld" defaultValue={editing.priority}>{["Critical","High","Medium","Low"].map(o => <option key={o}>{o}</option>)}</select>
            </Field>
            <Field label="Owner"><input id="eg-own" className="fld" defaultValue={editing.owner}/></Field>
            <Field label="Status" span={2}>
              <select id="eg-stat" className="fld" defaultValue={editing.status}>{["Open","In Progress","Closed"].map(o => <option key={o}>{o}</option>)}</select>
            </Field>
          </div>
        )}
      </Modal>
    </>
  );
}

function Sum({ label, value, color, sub }) {
  return (
    <div className="card tile-accent p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{label}</div>
      <div className="font-display text-3xl font-extrabold mt-1" style={{ color }}>{value}</div>
      <div className="font-mono text-[10px] text-zinc-500 mt-1">{sub}</div>
    </div>
  );
}
